import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Eye, Plus, Store, Trash2, Upload, Edit } from "lucide-react";
import { BulkUploadModal } from "./BulkUploadModal";
import { ViewStoreModal } from "./ViewStoreModal";
import { EditStoreDialog } from "./EditStoreDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AddStoreDialog } from "./AddStoreDialog";
import { IStore } from "@/interfaces/merchant.interfaces";
import { MerchantApi } from "@/api";
import { CSV_UPLOAD_TYPES } from "@/constants/common.constant";
import { PERMISSION_CODES } from "@/constants/permissions";
import { BUSINESS_TYPE } from "@/constants/merchant.constant";
import { useUserInfo } from "@/hooks/use-user-info";
import { usePermissions } from "@/hooks/use-permissions";
import { canManageMerchantStores } from "@/utils/role.utils";

interface StoresTabProps {
  initialStores: IStore[];
  merchantId: string;
  merchantCode: string;
  merchant?: any; // Add merchant data to access businessType
}

export function StoresTab({
  initialStores,
  merchantId,
  merchantCode,
  merchant,
}: StoresTabProps) {
  const { userInfo } = useUserInfo();
  const { hasPermission } = usePermissions();
  const canManageStores = canManageMerchantStores(
    userInfo?.roleCode,
    hasPermission(PERMISSION_CODES.UPDATE_MERCHANT)
  );

  const [stores, setStores] = useState(initialStores);

  // Modal states
  const [isAddStoreDialogOpen, setIsAddStoreDialogOpen] = useState(false);
  const [isEditStoreDialogOpen, setIsEditStoreDialogOpen] = useState(false);
  const [storeToEdit, setStoreToEdit] = useState<IStore | null>(null);
  const [isViewStoreModalOpen, setIsViewStoreModalOpen] = useState(false);
  const [isBulkStoreUploadOpen, setIsBulkStoreUploadOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState<IStore | null>(null);

  // Confirmation dialog states
  const [isDeleteStoreConfirmOpen, setIsDeleteStoreConfirmOpen] =
    useState(false);
  const [storeToDelete, setStoreToDelete] = useState<number | null>(null);

  // Handlers
  const handleAddStore = (newStore: IStore) => {
    setStores([...stores, newStore]);
  };

  const handleEditStore = (store: IStore) => {
    setStoreToEdit(store);
    setIsEditStoreDialogOpen(true);
  };

  const handleUpdateStore = (updatedStore: IStore) => {
    setStores(
      stores.map((store) =>
        store.id === updatedStore.id ? updatedStore : store
      )
    );
  };

  const handleBulkStoreUpload = async (newStores: any[]) => {
    try {
      // After bulk upload, fetch the updated list of stores
      const response = await MerchantApi.getStoresUsingMerchantId(merchantId);
      const mappedStores = (response?.data || []).map((store: any) => ({
        id: store.id,
        merchantVO: store.merchantVO,
        storeName: store.storeName,
        address: store.address,
        isEpos: store.isEpos,
        city: store.city,
        latitude: store.latitude,
        longitude: store.longitude,
        zipCode: store.zipCode,
        // Offline store fields
        contactNumber: store.contactNumber,
        mapLink: store.mapLink,
        email: store.email,
        storeImageUri: store.storeImageUri,
      }));
      setStores(mappedStores);
      toast.success(`Stores added successfully`);
    } catch (error) {
      toast.error(
        error?.message || "Failed to fetch updated stores after upload"
      );
    }
  };

  const handleViewStore = (store: IStore) => {
    setSelectedStore(store);
    setIsViewStoreModalOpen(true);
  };

  const confirmDeleteStore = (storeId: number) => {
    setStoreToDelete(storeId);
    setIsDeleteStoreConfirmOpen(true);
  };

  const handleDeleteStore = async () => {
    try {
      if (storeToDelete) {
        await MerchantApi.deleteMerchantStore(storeToDelete);
        setStores(stores.filter((store) => store.id !== storeToDelete));
        toast.success("Store deleted successfully");
        setIsDeleteStoreConfirmOpen(false);
        setStoreToDelete(null);
      }
    } catch (err) {
      toast.error(err?.message || "Failed to delete store");
    }
  };

  return (
    <Card className="border-0 shadow-sm bg-white">
      <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 bg-gray-50 p-6">
        <CardTitle className="text-lg font-medium text-gray-800">
          Stores
        </CardTitle>
        <div className="flex gap-2">
          {canManageStores && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="flex items-center gap-2"
                onClick={() => setIsBulkStoreUploadOpen(true)}
              >
                <Upload className="h-4 w-4" /> Bulk Upload
              </Button>
              <Button
                size="sm"
                className="flex items-center gap-2"
                onClick={() => setIsAddStoreDialogOpen(true)}
              >
                <Plus className="h-4 w-4" /> Add Store
              </Button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {stores.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="mx-auto w-32 h-32 mb-4">
              <img
                src="/lovable-uploads/4c75fc6c-0171-49e7-9e79-ed3e2d9d4c44.png"
                alt="No stores"
                className="w-full h-full object-contain"
              />
            </div>
            <p className="text-lg font-medium mb-1">No Stores Found</p>
            <p className="mb-4">
              No stores are currently added to this merchant.
            </p>
            {canManageStores && (
              <Button
                size="sm"
                variant="outline"
                className=" gap-2"
                onClick={() => setIsAddStoreDialogOpen(true)}
              >
                <Plus className="h-4 w-4" /> Create Your First Store
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stores.map((store) => {
              // Determine if this is an offline store based on merchant type and store fields
              const isOfflineStore =
                merchant?.businessType === BUSINESS_TYPE.OFFLINE ||
                store.contactNumber ||
                store.email ||
                store.mapLink ||
                store.storeImageUri;

              return (
                <Card
                  key={store.id}
                  className="border border-gray-100 shadow-sm"
                >
                  <CardContent className="p-4">
                    {isOfflineStore ? (
                      // Offline Store Card Layout
                      <>
                        {/* Store Image */}
                        {store.storeImageUri && (
                          <div className="w-full h-32 mb-3 overflow-hidden rounded-lg bg-gray-100">
                            <img
                              src={store.storeImageUri}
                              alt={store.storeName}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}

                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1 min-w-0 pr-2">
                            <h4 className="font-semibold text-gray-800 break-words">
                              {store?.storeName || "N/A"}
                            </h4>
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs mt-1 bg-purple-100 text-purple-800">
                              Offline Store
                            </span>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => handleViewStore(store)}
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            {canManageStores && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => handleEditStore(store)}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {canManageStores && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => confirmDeleteStore(store?.id)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Offline Store Details */}
                        <div className="space-y-2">
                          <p className="text-xs text-gray-500 line-clamp-2">
                            <span className="font-medium">Address:</span>{" "}
                            {store.address}
                          </p>
                          {store.email && (
                            <p className="text-xs text-gray-500 line-clamp-1">
                              <span className="font-medium">Email:</span>{" "}
                              {store.email}
                            </p>
                          )}
                          {store.contactNumber && (
                            <p className="text-xs text-gray-500 line-clamp-1">
                              <span className="font-medium">Contact:</span>{" "}
                              {store.contactNumber}
                            </p>
                          )}
                          {store.zipCode && (
                            <p className="text-xs text-gray-500 line-clamp-1">
                              <span className="font-medium">Zip Code:</span>{" "}
                              {store.zipCode}
                            </p>
                          )}
                        </div>
                      </>
                    ) : (
                      // Online Store Card Layout (existing)
                      <>
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1 min-w-0 pr-2">
                            <h4 className="font-semibold text-gray-800 break-words">
                              {store?.storeName || "N/A"}
                            </h4>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => handleViewStore(store)}
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            {canManageStores && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => handleEditStore(store)}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {canManageStores && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => confirmDeleteStore(store?.id)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 line-clamp-2">
                          {store.address}
                        </p>
                        <p className="text-xs text-gray-500 line-clamp-2">
                          {store?.zipCode ? `${store?.zipCode}, ` : ""}
                          {store?.city || ""}
                        </p>
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </CardContent>
      {/* Add Store Dialog */}
      <AddStoreDialog
        key={merchant?.businessType ?? "store"}
        open={isAddStoreDialogOpen}
        merchantId={merchantId}
        merchant={merchant}
        onOpenChange={setIsAddStoreDialogOpen}
        onAddStore={handleAddStore}
      />
      {/* Edit Store Dialog */}
      <EditStoreDialog
        open={isEditStoreDialogOpen}
        merchantId={merchantId}
        merchant={merchant}
        onOpenChange={setIsEditStoreDialogOpen}
        onUpdateStore={handleUpdateStore}
        storeToEdit={storeToEdit}
      />
      {/* Bulk Store Upload Modal */}
      <BulkUploadModal
        uploadType={CSV_UPLOAD_TYPES.STORE}
        merchantCode={merchantCode}
        open={isBulkStoreUploadOpen}
        onOpenChange={setIsBulkStoreUploadOpen}
        title="Bulk Upload Stores"
        type="store"
        onUploadComplete={handleBulkStoreUpload}
      />
      {/* View Store Modal */}
      <ViewStoreModal
        open={isViewStoreModalOpen}
        onOpenChange={setIsViewStoreModalOpen}
        store={selectedStore}
        merchant={merchant}
      />
      {/* Delete Store Confirmation */}
      <AlertDialog
        open={isDeleteStoreConfirmOpen}
        onOpenChange={setIsDeleteStoreConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Store</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this store? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteStore}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
